import { defineStore } from 'pinia';
import api from '@/services/api';

// Data is considered stale after 5 minutes
const STALE_TIME = 5 * 60 * 1000;

export const useVisitsStore = defineStore('visits', {
	state: () => ({
		visits: [],
		currentVisit: null,
		loading: false,
		error: null,
		lastFetched: null
	}),

	getters: {
		allVisits: (state) => state.visits,
		visitById: (state) => (id) => state.visits.find((visit) => visit.id === id),
		getCurrentVisit: (state) => state.currentVisit,
		upcomingVisits: (state) => {
			const now = new Date();
			return state.visits.filter((visit) => new Date(visit.when) > now).sort((a, b) => new Date(a.when) - new Date(b.when));
		},
		pastVisits: (state) => {
			const now = new Date();
			return state.visits.filter((visit) => new Date(visit.when) <= now).sort((a, b) => new Date(b.when) - new Date(a.when));
		},
		isLoading: (state) => state.loading,
		hasError: (state) => !!state.error,
		getError: (state) => state.error,
		isStale: (state) => !state.lastFetched || Date.now() - state.lastFetched > STALE_TIME,
		hasData: (state) => state.visits.length > 0
	},

	actions: {
		setVisits(visits) {
			this.visits = visits;
		},

		setCurrentVisit(visit) {
			this.currentVisit = visit;
		},

		setLoading(loading) {
			this.loading = loading;
		},

		setError(error) {
			this.error = error;
		},

		addVisit(visit) {
			this.visits.push(visit);
		},

		updateVisit(updatedVisit) {
			const index = this.visits.findIndex((visit) => visit.id === updatedVisit.id);
			if (index !== -1) {
				this.visits.splice(index, 1, updatedVisit);
			}
			if (this.currentVisit && this.currentVisit.id === updatedVisit.id) {
				this.currentVisit = updatedVisit;
			}
		},

		removeVisit(visitId) {
			this.visits = this.visits.filter((visit) => visit.id !== visitId);
			if (this.currentVisit && this.currentVisit.id === visitId) {
				this.currentVisit = null;
			}
		},

		async fetchVisits() {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.getVisits();
				this.setVisits(response.data);
				this.lastFetched = Date.now();
				return response.data;
			} catch (error) {
				this.setError(error.message || 'Failed to fetch visits');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		// Smart fetch - only fetches if data is missing or stale
		async ensureLoaded(force = false) {
			// Skip if already loading
			if (this.loading) return this.visits;

			// Skip if we have fresh data and not forcing
			if (!force && this.hasData && !this.isStale) {
				return this.visits;
			}

			return this.fetchVisits();
		},

		async fetchVisit(visitId) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.getVisit(visitId);
				this.setCurrentVisit(response.data);
				return response.data;
			} catch (error) {
				this.setError(error.message || `Failed to fetch visit with ID ${visitId}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async createVisit(visitData) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.createVisit(visitData);
				const newVisit = response.data;
				this.addVisit(newVisit);
				return newVisit;
			} catch (error) {
				this.setError(error.message || 'Failed to create visit');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async updateVisitAction({ id, visitData }) {
			this.setLoading(true);
			this.setError(null);

			try {
				const response = await api.updateVisit(id, visitData);
				const updatedVisit = response.data;
				this.updateVisit(updatedVisit);
				return updatedVisit;
			} catch (error) {
				this.setError(error.message || `Failed to update visit with ID ${id}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async deleteVisit(visitId) {
			this.setLoading(true);
			this.setError(null);

			try {
				await api.deleteVisit(visitId);
				this.removeVisit(visitId);
			} catch (error) {
				this.setError(error.message || `Failed to delete visit with ID ${visitId}`);
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async fetchCirclesVisits(circleId) {
			this.setLoading(true);
			this.setError(null);

			try {
				// Use server-side filtering instead of fetching all and filtering client-side
				const response = await api.getVisits({ circleId });
				this.setVisits(response.data);
				return response.data;
			} catch (error) {
				this.setError(error.message || 'Failed to fetch circle visits');
				throw error;
			} finally {
				this.setLoading(false);
			}
		},

		async addAttendeeToVisit({ visitId, userId }) {
			this.setLoading(true);
			this.setError(null);

			try {
				const visit = this.visits.find((v) => v.id === visitId);
				if (!visit) {
					throw new Error('Visit not found');
				}

				const updatedAttended = [...(visit.attended || []), userId];
				const response = await api.updateVisit(visitId, {
					...visit,
					attended: updatedAttended
				});

				this.updateVisit(response.data);
				return response.data;
			} catch (error) {
				this.setError(error.message || 'Failed to add attendee to visit');
				throw error;
			} finally {
				this.setLoading(false);
			}
		}
	}
});
